import { Fragment, ReactNode } from 'react';
import { faBan, faTruck } from '@fortawesome/pro-light-svg-icons';
import { faHouseChimney, faLock, faReceipt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Alert from '@v2/shared/components/Alert';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import ButtonLink from '@v2/shared/components/ButtonLink';
import ContentCard from '@v2/shared/components/ContentCard';
import FaqAccordion from '@v2/shared/components/FaqAccordion';
import { useSite } from '@v2/shared/lib/site';
import { cn } from '@v2/shared/lib/utils';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import Band from '../HelpCenter/Band';
import ContactForm from './ContactForm';
import Topic, {
  ANCHOR_OFFSET,
  TopicButtonLink,
  TopicGroup,
  TopicLink,
  TopicMessageLink,
  trackTopicClick,
} from './Topic';
import useContactForm from './useContactForm';
import {
  DELIVERY_ESTIMATES,
  DISCOUNTLOCK_URL,
  JUMP_LINKS,
  ORDERS_URL,
  phoneLines,
  REASON,
  SIGN_IN_URL,
} from './constants';
import type { ContactPageProps } from './types';

const H2 = 'mb-0 mt-0 text-2xl font-extrabold tracking-tight text-slate-900';
const TAGLINE = 'mb-4 mt-1 text-sm text-slate-500';
const MICRO = 'text-[11px] font-bold uppercase tracking-wider';
const OUTLINE = 'border border-solid border-primary bg-white text-primary hover:bg-primary-50';

const INTRO =
  'Most things, from checking an order to turning off a renewal, take about a minute in your ' +
  "account. For everything else, pick a topic below and we'll get you to the right answer or the " +
  'right person.';

/**
 * /contact, for DiscountMags .com, .ca and .com.au.
 *
 * The page answers before it asks. Four topics carry the messages people actually send,
 * each opening with the thing that fixes it without writing to anyone -- the account page,
 * the help article -- and only then offering the form, with the reason already chosen. The
 * form is still the whole point of the page; it is just no longer the first thing on it.
 *
 * TopMags, MagSpot and MagZoom keep their own /contact. Theirs are domain overrides and
 * this is the shared template, which on this page only the three DiscountMags storefronts
 * reach: their override chains are single-element and disjoint, so `discountmags.ca` and
 * `discountmags.com.au` never see a file under `domain-overrides/discountmags.com/`.
 */
export default function Contact({
  subjects,
  customer,
  sent,
  initialSubject,
  serviceNotice,
}: ContactPageProps) {
  const { buildUrl } = useUrlGenerator();
  const { country } = useSite();

  // Hand-written paths, so they go through buildUrl: the AU storefront serves from a base
  // directory and a bare path drops its visitors onto the .com site.
  const ordersUrl = buildUrl(ORDERS_URL);
  const discountLockUrl = buildUrl(DISCOUNTLOCK_URL);
  const helpUrl = buildUrl('/help');

  // Owned here rather than inside the form: the topic cards are several screens above it
  // and each of them selects a reason in it.
  const form = useContactForm({ customer, sent, initialSubject });

  const glance: { href: string; title: string; text: ReactNode }[] = [
    {
      href: ordersUrl,
      title: 'My Orders',
      text: (
        <>
          Check status, <b>change an address</b>, cancel an order
        </>
      ),
    },
    {
      href: discountLockUrl,
      title: 'DiscountLock',
      text: (
        <>
          <b>Turn off or delay a renewal</b>, update your card
        </>
      ),
    },
    {
      href: '#signin',
      title: "Can't sign in?",
      text: (
        <>
          No password needed. <b>Use the email you ordered with</b>
        </>
      ),
    },
    {
      href: helpUrl,
      title: 'Help Center',
      text: (
        <>
          Answers to the <b>most common questions</b>, no sign-in needed
        </>
      ),
    },
  ];

  return (
    <>
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Contact Us' }]} />

      {/* Holiday hours, a publisher delay, an outage. Rendered only when an admin has put
          something in the snippet -- an empty warning band on a support page reads as the
          site being broken. */}
      {serviceNotice ? (
        <Alert color="warning">
          <div dangerouslySetInnerHTML={{ __html: serviceNotice }} />
        </Alert>
      ) : null}

      <Band
        hero
        title="How can we help?"
        intro={INTRO}
        searchAction="/help/search"
        popular={[
          { href: buildUrl('/help/why-was-i-charged'), label: 'Why was I charged?' },
          { href: '#delivery', label: "Where's my first issue?" },
          { href: buildUrl('/help/cancel-vs-turn-off-renewal'), label: 'Cancel vs. turn off renewal' },
          { href: buildUrl('/help/find-order-number'), label: 'Find my order number' },
        ]}
      />

      <ul
        className={cn(
          'relative z-10 mx-3 -mt-14 grid list-none grid-cols-2 p-0 md:mx-5 md:grid-cols-4',
          'overflow-hidden rounded-xl border border-solid border-slate-200 bg-white shadow-sm'
        )}
      >
        {glance.map((item, index) => (
          <li
            key={item.title}
            className={cn(
              'border-0 border-solid border-slate-200',
              index % 2 === 1 && 'border-l',
              index >= 2 && 'border-t md:border-t-0',
              index > 0 && 'md:border-l'
            )}
          >
            <a
              href={item.href}
              className="block h-full px-4 py-3.5 no-underline hover:bg-slate-50"
              onClick={() => trackTopicClick('At a glance', item.title)}
            >
              <span className="block text-sm font-extrabold text-slate-900">{item.title}</span>
              <span className="mt-0.5 block text-xs leading-snug text-slate-500 [&>b]:text-primary">
                {item.text}
              </span>
            </a>
          </li>
        ))}
      </ul>

      <nav aria-label="On this page" className="mt-6 flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold text-slate-500">On this page:</span>
        {JUMP_LINKS.map(([href, label]) => (
          <a
            key={href}
            href={href}
            className="rounded-full border border-solid border-slate-200 px-3.5 py-1 font-semibold text-slate-700 no-underline hover:border-primary hover:text-primary"
          >
            {label}
          </a>
        ))}
      </nav>

      <div className="mt-5 flex flex-col gap-6">
        <ContentCard id="topics" className={ANCHOR_OFFSET}>
          <p className={cn('mb-2.5 mt-0 inline-block rounded-full bg-primary-50 px-2.5 py-1 text-primary', MICRO)}>
            Start here
          </p>
          <h2 className={H2}>What do you need help with?</h2>
          <p className={TAGLINE}>
            Each topic shows the fastest fix first. If that doesn&apos;t solve it, send us a message
            from the same card and we&apos;ll already know what it&apos;s about.
          </p>

          <div className="grid gap-4 md:grid-cols-2">
            <Topic
              hot
              icon={faReceipt}
              title="A charge I don't recognize"
              actions={
                <>
                  <TopicButtonLink href={discountLockUrl}>See My Renewals</TopicButtonLink>
                  <TopicLink href={buildUrl('/help/why-was-i-charged')}>Why was I charged?</TopicLink>
                  <TopicMessageLink reason={REASON.charge} onPick={form.pickReason}>
                    Message us about a charge
                  </TopicMessageLink>
                </>
              }
            >
              It&apos;s almost always a DiscountLock renewal of a subscription you ordered a while
              ago. Renewals run on your order anniversary, not the date on your label, and the new
              term is added to the end of your current one. We email you 30 days and 7 days before
              each renewal.
            </Topic>

            <Topic
              id="delivery"
              icon={faTruck}
              title="Where's my magazine?"
              actions={
                <>
                  <TopicButtonLink href={ordersUrl}>Check My Delivery Estimate</TopicButtonLink>
                  <TopicLink href={buildUrl('/help/when-will-i-receive-my-first-issue')}>
                    When will my first issue arrive?
                  </TopicLink>
                  <TopicMessageLink reason={REASON.missedIssue} onPick={form.pickReason}>
                    Report a missing issue
                  </TopicMessageLink>
                </>
              }
            >
              <p className="m-0">
                First issues take time because publishers print mailing labels weeks ahead. After the
                first issue, the rest arrive on the publisher&apos;s normal schedule.
              </p>
              <dl className="m-0 mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 rounded-lg border border-solid border-slate-200 bg-slate-50 px-3.5 py-2.5 tabular-nums">
                {DELIVERY_ESTIMATES.map(([frequency, estimate]) => (
                  <Fragment key={frequency}>
                    <dt className="m-0 text-slate-500">{frequency}</dt>
                    <dd className="m-0 font-bold text-slate-900">{estimate}</dd>
                  </Fragment>
                ))}
              </dl>
            </Topic>

            <Topic
              icon={faBan}
              title="Cancel, or just stop it renewing?"
              actions={
                <>
                  <TopicButtonLink href={discountLockUrl} className={OUTLINE}>
                    Turn Off DiscountLock
                  </TopicButtonLink>
                  <TopicButtonLink href={ordersUrl}>Cancel from My Orders</TopicButtonLink>
                  <TopicMessageLink reason={REASON.cancel} onPick={form.pickReason}>
                    Message us about a cancellation
                  </TopicMessageLink>
                </>
              }
            >
              <b className="text-slate-900">Stop future renewals, keep my issues:</b> turn off
              DiscountLock for that subscription and your current term continues.{' '}
              <b className="text-slate-900">Cancel now:</b> cancel from My Orders for a pro-rated
              refund of unsent issues within 6 months of the order.
            </Topic>

            <Topic
              icon={faHouseChimney}
              title="Change my address or fix a mistake"
              actions={
                <>
                  <TopicButtonLink href={ordersUrl}>Update in My Orders</TopicButtonLink>
                  <TopicLink href={buildUrl('/help/change-address')}>
                    How do I change my address?
                  </TopicLink>
                  <TopicMessageLink reason={REASON.address} onPick={form.pickReason}>
                    Message us about an address
                  </TopicMessageLink>
                </>
              }
            >
              Update the address on each subscription in My Orders. Publishers print labels 6 to 8
              weeks ahead, so the change shows up on a later issue. Caught a typo right after
              ordering? Fix it now, before the order reaches the publisher.
            </Topic>
          </div>

          <TopicGroup title="Other topics">
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-0 border-t border-solid border-slate-200 pt-4 text-sm">
              <span className="font-semibold text-slate-500">Other topics:</span>
              <TopicLink href={buildUrl('/help/access-digital-magazine')}>Digital magazines</TopicLink>
              <TopicMessageLink reason={REASON.duplicates} onPick={form.pickReason}>
                Duplicate copies
              </TopicMessageLink>
              <TopicLink href={buildUrl('/help/book-orders')}>Book orders and returns</TopicLink>
              <TopicLink href={discountLockUrl}>Update my card</TopicLink>
              <TopicMessageLink reason={REASON.coupon} onPick={form.pickReason}>
                A coupon didn&apos;t apply
              </TopicMessageLink>
              {/* US only. /bulk-order resolves to contact_bulkorder.html, which exists
                  only under domain-overrides/discountmags.com/, so the link 404s on CA
                  and AU -- and this page is the shared template all three reach. Gated
                  the way Footer.tsx gates the same destination. The dropdown still
                  carries reason 17 for anyone on those storefronts who needs it. */}
              {country === 'US' && <TopicLink href={buildUrl('/bulk-order')}>Bulk orders</TopicLink>}
              <TopicLink href={buildUrl('/affiliate-program')}>Affiliates</TopicLink>
              <TopicMessageLink reason={REASON.publishers} onPick={form.pickReason}>
                Publishers
              </TopicMessageLink>
            </div>
          </TopicGroup>
        </ContentCard>

        <ContentCard id="message" className={ANCHOR_OFFSET}>
          <h2 className={H2}>Send us a message</h2>
          <p className={TAGLINE}>
            Pick the topic that matches your question so it reaches the right person. Our team reads
            every message and replies by email.
          </p>

          <ContactForm subjects={subjects} form={form} />
        </ContentCard>

        <ContentCard id="phone" className={ANCHOR_OFFSET}>
          <h2 className={H2}>Prefer to call?</h2>
          <p className={TAGLINE}>Phone support is reserved for existing customers.</p>
          <p className="mb-4 mt-0 text-slate-700">
            Your support number is in your order confirmation email and next to the DiscountMags
            charge on your card or PayPal statement.
          </p>

          <div className="grid gap-3 md:grid-cols-2">
            {phoneLines().map(([line, hours, detail]) => (
              <div
                key={line}
                className="rounded-lg border border-solid border-slate-200 bg-slate-50 px-3.5 py-3"
              >
                <p className={cn('m-0 text-slate-500', MICRO)}>{line}</p>
                <p className="m-0 mt-0.5 text-lg font-extrabold text-slate-900">{hours}</p>
                <p className="m-0 mt-0.5 text-xs leading-snug text-slate-500">{detail}</p>
              </div>
            ))}
          </div>

          <p className="mb-0 mt-4 text-slate-700">
            For the security of your payment details we don&apos;t take orders or card numbers by
            phone. If checkout isn&apos;t working, call during live hours and an agent will walk you
            through it on the website, or{' '}
            <a className="text-primary" href="#message">
              send us a message
            </a>
            .
          </p>
        </ContentCard>

        <div
          id="signin"
          className={cn(
            'flex items-start gap-3.5 rounded-lg border border-solid border-primary-100 bg-primary-50 p-5',
            ANCHOR_OFFSET
          )}
        >
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-primary text-white">
            <FontAwesomeIcon icon={faLock} />
          </span>
          <div>
            <p className="m-0 font-bold text-slate-900">Can&apos;t sign in, or can&apos;t find your order?</p>
            <p className="m-0 mt-1 text-sm text-slate-700">
              You don&apos;t need a password. Sign in with Google, Apple or Facebook, or get a
              one-time code by email. Use the same email address you ordered with; your order
              confirmation shows which one. Gift orders are under the buyer&apos;s email. If you
              still can&apos;t find your order,{' '}
              <a className="text-primary underline" href="#message">
                send us a message
              </a>{' '}
              with the name and ZIP on the subscription and we&apos;ll locate it.
            </p>
            <ButtonLink href={buildUrl(SIGN_IN_URL)} className="mt-3 normal-case tracking-normal">
              Sign In
            </ButtonLink>
          </div>
        </div>

        <ContentCard id="faqs" className={ANCHOR_OFFSET}>
          <h2 className="mb-4 mt-0 text-2xl font-extrabold tracking-tight text-slate-900">
            Quick answers before you write in
          </h2>

          <div className="grid gap-2.5">
            <FaqAccordion
              defaultOpen
              question="Why was I charged?"
              answer={
                <>
                  Most charges people don&apos;t recognize are DiscountLock renewals. Every eligible
                  magazine subscription renews through DiscountLock so you never miss an issue, at a
                  price at least 10% below the publisher&apos;s list price. Renewals run on your
                  order anniversary, not the expiration date on your label, and the new term is
                  added to the end of your current one. We email you 30 days and 7 days before. You
                  can turn it off for any subscription in{' '}
                  <a className="text-primary underline" href={discountLockUrl}>
                    your account
                  </a>
                  .{' '}
                  <a className="text-primary underline" href={buildUrl('/help/why-was-i-charged')}>
                    Read more
                  </a>
                </>
              }
            />
            <FaqAccordion
              question="Is the number on my magazine label my order number?"
              answer={
                <>
                  No. That is the publisher&apos;s subscriber number and we can&apos;t look up your
                  order with it. Your DiscountMags order number is in your confirmation email and
                  under{' '}
                  <a className="text-primary underline" href={ordersUrl}>
                    My Orders
                  </a>
                  .
                </>
              }
            />
            <FaqAccordion
              question="What's the difference between cancelling and turning off DiscountLock?"
              answer={
                <>
                  Turning off DiscountLock stops future renewals; your current term continues and
                  nothing is refunded. Cancelling ends the order now and refunds the issues that
                  haven&apos;t been sent yet, within 6 months of the order. Once DiscountLock is off
                  it can&apos;t be turned back on for that subscription.{' '}
                  <a
                    className="text-primary underline"
                    href={buildUrl('/help/cancel-vs-turn-off-renewal')}
                  >
                    Read more
                  </a>
                </>
              }
            />
            <FaqAccordion
              question="How long do refunds take?"
              answer={
                <>
                  We process refunds within 48 hours and most banks post them within 5 to 7 business
                  days, to the payment method you used. If you&apos;ve opened a dispute with your
                  bank or PayPal, the order is locked until the dispute closes, so contacting us
                  first is faster.{' '}
                  <a className="text-primary underline" href={buildUrl('/refund-policy')}>
                    Refund policy
                  </a>
                </>
              }
            />
            <FaqAccordion
              question="Who handles delivery, you or the publisher?"
              answer={
                <>
                  We take your order and payment; the publisher prints, labels and mails every
                  issue. That&apos;s why first issues take 6 to 12 weeks and address changes take 6
                  to 8 weeks to reach your label. If an issue is missing, message us and we&apos;ll
                  open an investigation with the publisher.{' '}
                  <a
                    className="text-primary underline"
                    href={buildUrl('/help/discountmags-vs-publisher')}
                  >
                    Read more
                  </a>
                </>
              }
            />
            <FaqAccordion
              question="Why am I getting two copies of the same magazine?"
              answer={
                <>
                  The publisher has two subscriptions on file for you, usually because a name or
                  address didn&apos;t match exactly.{' '}
                  <a className="text-primary underline" href="#message">
                    Send us a message
                  </a>{' '}
                  with the name and address on each label and we&apos;ll ask the publisher to
                  combine them, so nothing you paid for is wasted.{' '}
                  <a className="text-primary underline" href={buildUrl('/help/duplicate-issues')}>
                    Read more
                  </a>
                </>
              }
            />
          </div>
        </ContentCard>
      </div>
    </>
  );
}

Contact.layoutName = 'Layouts.Institutional';
